import type { ReactNode } from "react";
import {
	FiActivity,
	FiAlertTriangle,
	FiClock,
	FiEye,
	FiLock,
	FiSlash,
	FiX,
} from "react-icons/fi";

import { cn } from "utils/helpers";

import { RBAC_STRINGS } from "../../constants/rbacStrings";
import type { EventGrantDisplayState } from "../../types/eventGrant.type";

/**
 * Presents an event-grant / panel row's server-authoritative display state
 * (AC3). Distinct treatments for pending-acceptance, active, auto-released,
 * plain read-only, COI-suspended and COI-flagged so a reviewer can tell them
 * apart at a glance — this is the single source of truth for that mapping.
 */

interface EventGrantStatusBadgeProps {
	state: EventGrantDisplayState;
	className?: string;
}

const STATE_ICON: Record<EventGrantDisplayState, ReactNode> = {
	pending: <FiClock size={12} aria-hidden="true" />,
	accepted: <FiActivity size={12} aria-hidden="true" />,
	released: <FiLock size={12} aria-hidden="true" />,
	read_only: <FiEye size={12} aria-hidden="true" />,
	suspended: <FiSlash size={12} aria-hidden="true" />,
	flagged: <FiAlertTriangle size={12} aria-hidden="true" />,
	declined: <FiX size={12} aria-hidden="true" />,
	revoked: <FiX size={12} aria-hidden="true" />,
};

const STATE_CLASSES: Record<EventGrantDisplayState, string> = {
	pending: "bg-amber-100 text-amber-700",
	accepted: "bg-[#e8eeff] text-[#0031DD]",
	released: "bg-purple-100 text-purple-700",
	read_only: "bg-gray-100 text-gray-600",
	suspended: "bg-orange-100 text-orange-700",
	flagged: "bg-red-100 text-red-700",
	declined: "bg-red-100 text-red-700",
	revoked: "bg-gray-200 text-gray-500",
};

const STATE_LABEL: Record<EventGrantDisplayState, string> = {
	pending: RBAC_STRINGS.state.pending,
	accepted: RBAC_STRINGS.state.accepted,
	released: RBAC_STRINGS.state.released,
	read_only: RBAC_STRINGS.state.readOnly,
	suspended: RBAC_STRINGS.state.suspended,
	flagged: RBAC_STRINGS.state.flagged,
	declined: RBAC_STRINGS.state.declined,
	revoked: RBAC_STRINGS.state.revoked,
};

export const EventGrantStatusBadge = ({
	state,
	className,
}: EventGrantStatusBadgeProps) => {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
				STATE_CLASSES[state],
				className
			)}
			data-testid={`event-grant-status-badge-${state}`}
		>
			{STATE_ICON[state]}
			{STATE_LABEL[state]}
		</span>
	);
};
